"use client";
import { create } from "zustand";
import { TicketType, SuccessPageType } from "@/types/strapiContentType";

interface FaqItem {
  title: string;
  text: string;
}

interface OperatorColor {
  operator: string;
  color: string;
}

interface DiscountBadge {
  operator: string;
  image: string;
}

interface CustomContactUsActionButton {
  id: number;
  Type: string;
  Value: string;
}

interface CustomContactUsItem {
  id: number;
  Title: string;
  Description: string;
  Observation: string;
  Icon: {
    data: {
      attributes: {
        url: string;
        alternativeText: string | null;
      };
    };
  };
  actionButton: CustomContactUsActionButton;
}

// Novos tipos para cache e loading
interface CacheEntry<T = any> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live em ms
}

interface LoadingState {
  [key: string]: boolean; // operatorId -> isLoading
}

interface StrapStoreState {
  primaryColor: string;
  primaryTextColorContrast: string;
  logoHeaderUrlImage: string;
  logoHeaderMobileUrlImage: string;
  secondaryColor: string;
  darkSecondaryColor: string;
  secondaryTextColorContrast: string;
  white: string;
  footerTitleColumn: string;
  footerItemColumn: string;
  cancelCardText: string;
  faqItems: FaqItem[];
  logoSvg: string;
  headerLogo: string;
  headerBackgroundColor: string;
  headerInputBackgroundColor: string;
  logoFooter: string;
  isColorsLoaded: boolean;
  setIsColorsLoaded: (loaded: boolean) => void;
  setColor: (key: keyof StrapStoreState, value: string) => void;
  premiumBgColor: OperatorColor[];
  DiscountBadge: DiscountBadge[];
  authToken: string;
  redirect_home_url: string;
  buttonColor: string;
  headerButtonTextColor: string;
  operatorId: string | null;
  setOperatorId: (id: string | null) => void;
  cmsId: string | null;
  setCmsId: (id: string | null) => void;
  cmsUsername: string | null;
  setCmsUsername: (cmsUsername: string | null) => void;
  tiketStrapi: TicketType[] | null;
  setTiketStrapi: (tiketStrapi: TicketType[] | null) => void;
  logoModal: string | null;
  setLogoModal: (logoModal: string | null) => void;
  successPageContent: SuccessPageType | null;
  setSuccessPageContent: (successPageContent: SuccessPageType | null) => void;
  isBannerSuccessPage: boolean;
  setIsBannerSuccessPage: (isBannerSuccessPage: boolean) => void;
  headerInputTextColor: string;
  CustomContactUs: CustomContactUsItem[] | null;
  setCustomContactUs: (customContactUs: CustomContactUsItem[] | null) => void;
  filters_premium: any[];
  setFiltersPremium: (filtersPremium: any[]) => void;
  show_filters_premium: boolean;
  setShowFiltersPremium: (showFiltersPremium: boolean) => void;
  gratuityShow: boolean;
  setGratuityShow: (gratuityShow: boolean) => void;
  activeInsurance: boolean;
  setActiveInsurance: (activeInsurance: boolean) => void;
  stepBackgroundColor: string;
  newSearchButtonBackgroundColor: string;
  couponButtonBackgroundColor: string;
  loginButtonBackgroundColor: string;
  profileBackgroundColor: string;
  iconArrowBackgroundColor: string;
  cleanFiltersBackgroundColor: string;
  timerBackgroundColor: string;
  continueButtonBackgroundColor: string;
  continueButtonTextColor: string;
  seatBackgroundColor: string;
  floorButtonBackgroundColor: string;
  seatSelectedTextColor: string;
  seo_routes: any[];
  setSeoRoutes: (seoRoutes: any[]) => void;
  show_seo_routes: boolean;
  setShowSeoRoutes: (showSeoRoutes: boolean) => void;

  // Novos estados para cache e loading
  webResourcesCache: Map<string, CacheEntry>;
  loadingStates: LoadingState;
  activePromises: Map<string, Promise<any>>;
  defaultCacheTTL: number; // 5 minutos por padrão

  // Novos métodos para cache
  setWebResourcesCache: (key: string, data: any, ttl?: number) => void;
  getWebResourcesCache: (key: string) => any | null;
  clearWebResourcesCache: (key?: string) => void;
  setLoadingState: (operator: string, isLoading: boolean) => void;
  getLoadingState: (operator: string) => boolean;
  setActivePromise: (key: string, promise: Promise<any>) => void;
  getActivePromise: (key: string) => Promise<any> | null;
  removeActivePromise: (key: string) => void;
}

const useStrapiStore = create<StrapStoreState>((set, get) => ({
  primaryColor: "#282828",
  primaryTextColorContrast: "#F2F200",
  logoHeaderUrlImage: "",
  logoHeaderMobileUrlImage: "",
  redirect_home_url: "/",
  logoFooterUrlImage: "",
  buttonColor: "#F2F200",
  isColorsLoaded: true,
  setIsColorsLoaded: (loaded) => set((state) => ({ isColorsLoaded: loaded })),
  DiscountBadge: [
    {
      operator: "Brasil Sul",
      image:
        "https://embaracaai-strapi.s3.sa-east-1.amazonaws.com/Rectangle_1_420d4739e9.png",
    },
    {
      operator: "Expresso Princesa dos Campos",
      image:
        "https://embaracaai-strapi.s3.sa-east-1.amazonaws.com/Rectangle_1_420d4739e9.png",
    },
  ],
  secondaryColor: "#F2F200",
  darkSecondaryColor: "#282828",
  secondaryTextColorContrast: "#282828",
  white: "#ffffff",
  footerTitleColumn: "#D7D8D8",
  footerItemColumn: "#B0B0B1",
  headerLogo: "",
  headerBackgroundColor: "#ffffff",
  headerInputBackgroundColor: "#f8f880",
  logoSvg: "",
  logoFooter: "",
  authToken: "",
  cancelCardText: `<div>
    <p>Sua passagem pode ser cancelada <b>até 3 horas</b> antes do início da viagem.</p>
    <br>
    <p>Mas <b>atenção,</b> alguns estados possuem <b>prazos diferentes:</b> em <b>Minas Gerais</b> você pode pedir o cancelamento <b>até 12 horas</b> antes da viagem. Já no Estado do <b>Pará</b> o prazo de cancelamento é de <b>até 8 horas.</b></p>
    <br>
    <p>Se você já imprimiu ou alterou o bilhete no guichê da operadora, a sua compra <b>não pode ser</b> cancelada pelo nosso aplicativo ou site. Verifique a possibilidade diretamente com a operadora.</p>
  </div>
  <br>
  <p>Para cancelar só uma parte da sua compra, entre em contato com o nosso suporte de atendimento ao cliente. Vamos te ajudar!</p>`,
  faqItems: [],
  setColor: (key, value) => set((state) => ({ ...state, [key]: value })),
  premiumBgColor: [{ operator: "Brasil Sul", color: "#4F4F4F" }],
  headerButtonTextColor: "#ffffff",
  operatorId: null,
  setOperatorId: (operatorId) => set((state) => ({ operatorId: operatorId })),
  cmsId: null,
  setCmsId: (cmsId) => set((state) => ({ cmsId: cmsId })),
  cmsUsername: null,
  setCmsUsername: (cmsUsername) =>
    set((state) => ({ cmsUsername: cmsUsername })),
  tiketStrapi: null,
  setTiketStrapi: (tiketStrapi) =>
    set((state) => ({ tiketStrapi: tiketStrapi })),
  logoModal: null,
  setLogoModal: (logoModal) => set((state) => ({ logoModal: logoModal })),
  headerInputTextColor: "#FFFFFF",
  successPageContent: null,
  setSuccessPageContent: (successPageContent) =>
    set((state) => ({ successPageContent: successPageContent })),
  isBannerSuccessPage: true,
  setIsBannerSuccessPage: (isBannerSuccessPage) =>
    set((state) => ({ isBannerSuccessPage: isBannerSuccessPage })),
  CustomContactUs: null,
  setCustomContactUs: (customContactUs) =>
    set((state) => ({ CustomContactUs: customContactUs })),
  filters_premium: [],
  setFiltersPremium: (filtersPremium) =>
    set((state) => ({ filters_premium: filtersPremium })),
  show_filters_premium: true,
  setShowFiltersPremium: (showFiltersPremium) =>
    set((state) => ({ show_filters_premium: showFiltersPremium })),
  gratuityShow: false,
  setGratuityShow: (gratuityShow) =>
    set((state) => ({ gratuityShow: gratuityShow })),
  activeInsurance: true,
  setActiveInsurance: (activeInsurance) =>
    set((state) => ({ activeInsurance: activeInsurance })),
  stepBackgroundColor: "#000000",
  newSearchButtonBackgroundColor: "#000000",
  couponButtonBackgroundColor: "#000000",
  loginButtonBackgroundColor: "#000000",
  profileBackgroundColor: "#000000",
  iconArrowBackgroundColor: "#000000",
  cleanFiltersBackgroundColor: "#000000",
  timerBackgroundColor: "#000000",
  continueButtonBackgroundColor: "#000000",
  continueButtonTextColor: "#000000",
  seatBackgroundColor: "#000000",
  floorButtonBackgroundColor: "#000000",
  seatSelectedTextColor: "#000000",
  seo_routes: [],
  setSeoRoutes: (seoRoutes) => set((state) => ({ seo_routes: seoRoutes })),
  show_seo_routes: true,
  setShowSeoRoutes: (showSeoRoutes) =>
    set((state) => ({ show_seo_routes: showSeoRoutes })),

  // Novos estados para cache e loading
  webResourcesCache: new Map(),
  loadingStates: {},
  activePromises: new Map(),
  defaultCacheTTL: 5 * 60 * 1000, // 5 minutos

  setWebResourcesCache: (key: string, data: any, ttl?: number) => {
    const state = get();
    const cacheEntry: CacheEntry = {
      data,
      timestamp: Date.now(),
      ttl: ttl || state.defaultCacheTTL,
    };
    const newCache = new Map(state.webResourcesCache);
    newCache.set(key, cacheEntry);
    set({ webResourcesCache: newCache });
  },

  getWebResourcesCache: (key: string) => {
    const state = get();
    const entry = state.webResourcesCache.get(key);

    if (!entry) return null;

    // Verificar se expirou
    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      // Cache expirado, remover
      const newCache = new Map(state.webResourcesCache);
      newCache.delete(key);
      set({ webResourcesCache: newCache });
      return null;
    }

    return entry.data;
  },

  clearWebResourcesCache: (key?: string) => {
    const state = get();
    if (key) {
      const newCache = new Map(state.webResourcesCache);
      newCache.delete(key);
      set({ webResourcesCache: newCache });
    } else {
      set({ webResourcesCache: new Map() });
    }
  },

  setLoadingState: (operator: string, isLoading: boolean) => {
    set((state) => ({
      loadingStates: {
        ...state.loadingStates,
        [operator]: isLoading,
      },
    }));
  },

  getLoadingState: (operator: string) => {
    return get().loadingStates[operator] || false;
  },

  setActivePromise: (key: string, promise: Promise<any>) => {
    const state = get();
    const newPromises = new Map(state.activePromises);
    newPromises.set(key, promise);
    set({ activePromises: newPromises });
  },

  getActivePromise: (key: string) => {
    return get().activePromises.get(key) || null;
  },

  removeActivePromise: (key: string) => {
    const state = get();
    const newPromises = new Map(state.activePromises);
    newPromises.delete(key);
    newPromises.delete(key);
    set({ activePromises: newPromises });
  },
}));

export default useStrapiStore;
